<template>
  <FriedModalSubContainer>
    <template #header>
      <FriedModalHeader :header="t('cr_create_document_from_template')">
        <template #custom>
          <FriedBreadcrumb v-if="breadcrumbs.length > 0" :collapse-at="3" class="breadcrumbs">
            <FriedBreadcrumbItem clickable hide-icon @click="$emit('select-folder', '/')">
              {{ $t('folders') }}
            </FriedBreadcrumbItem>
            <FriedBreadcrumbItem
              v-for="(breadcrumb, index) in breadcrumbs"
              :key="breadcrumb.label"
              clickable
              class="breadcrumb"
              @click="
                $emit(
                  'select-folder',
                  `/${DirectoryHelper.createPathFromBreadcrumbs(breadcrumbs, index)}`
                )
              "
            >
              {{ breadcrumb.label }}
            </FriedBreadcrumbItem>
          </FriedBreadcrumb>
        </template>
      </FriedModalHeader>
    </template>
    <div class="create-document-content-library">
      <template v-if="!!visibleDirectories?.length">
        <div
          v-if="!isRoot && directories.length > initialDisplayFolderCount"
          class="button-container"
        >
          <button
            class="toggle-button"
            data-external="create-document-show-all-folders-button"
            @click="showAllFolders = !showAllFolders"
          >
            <FriedH4>
              {{
                showAllFolders ? t('contracts_show_less_folders') : t('contracts_show_more_folders')
              }}
            </FriedH4>
            <FriedIcon :class="['chevron-icon', { up: showAllFolders }]" icon="chevron-down" />
          </button>
        </div>
        <TransitionFadeInOut>
          <div :class="['folder-outer-container', { fetching }]">
            <CreateDocumentItemCard
              v-for="directory in visibleDirectories"
              :key="directory.id"
              icon="folder-open"
              :label="directory.title"
              :show-icon-background="false"
              :data-external="`create-document-folder-${directory.id}`"
              @click="$emit('select-folder', createPath(directory))"
            />
          </div>
        </TransitionFadeInOut>
      </template>
      <CreateDocumentTemplateTable
        overflow="initial"
        :directory="path"
        @select="$emit('select', $event)"
      />
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <template #left>
          <FriedButton
            data-external="create-document-upload-back-button"
            button-type="tertiary"
            @click="$emit('back')"
          >
            <template #leftIcon>
              <FriedIcon icon="arrow-left" />
            </template>
            {{ t('back') }}
          </FriedButton>
        </template>
      </FriedModalButtonsFooter>
    </template>
  </FriedModalSubContainer>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { Resource } from '@getaccept/lib-shared/src/resources/types/resource';
import { computed, ref } from 'vue';
import type { Directory } from '@getaccept/lib-shared/src/types/directory';
import { DirectoryHelper } from '../../content-library/directories/helpers/directory.helper';
import CreateDocumentTemplateTable from './CreateDocumentTemplateTable.vue';
import CreateDocumentItemCard from './CreateDocumentItemCard.vue';

const { t } = useI18n();

const props = defineProps<{
  directories: Directory[];
  path: string;
  fetching: boolean;
}>();

defineEmits<{
  select: [Resource];
  back: [];
  directories: [Directory[]];
  'select-folder': [string];
}>();

const { createPath } = DirectoryHelper;
const isRoot = computed(() => props.path === '/');

const initialDisplayFolderCount = 6;
const showAllFolders = ref(false);

const visibleDirectories = computed(() =>
  isRoot.value || showAllFolders.value
    ? props.directories
    : props.directories?.slice(0, initialDisplayFolderCount)
);

const breadcrumbs = computed(() =>
  props.path
    .split('/')
    .filter(Boolean)
    .map(label => ({
      label,
    }))
);
</script>

<style lang="scss" scoped>
@import '../../folders/assets/folders';

.breadcrumbs {
  margin: var(--spacing-100) 0;
}

.create-document-content-library {
  .folder-outer-container {
    margin-bottom: var(--spacing-150);
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    border: 2px solid transparent;
  }

  .button-container {
    position: sticky;
    top: 0;
    width: 100%;
    padding: var(--spacing-100) 0;
    background: white;
    z-index: 1;

    .toggle-button {
      background: inherit;
      border: 0;
      margin: 0 0 0 auto;
      display: block;
      display: flex;
      gap: var(--spacing-50);
      color: var(--text-gray);
      cursor: pointer;

      .chevron-icon {
        transition: all 0.2s ease-in-out;

        &.up {
          transform: rotate(180deg);
        }
      }
    }
  }
}
</style>
